import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { FaBuilding, FaMapMarkerAlt, FaInfoCircle, FaSpinner } from "react-icons/fa";
import { Clock } from "lucide-react";
import { store } from "../../../Store/store";
import * as HVTRegistrationService from "../../../Services/HVTRegistration/HVTRegistrationService";
import * as B2BRegistrationService from "../../../Services/B2BRegistration/B2BRegistrationService";
import { formatDateOnly, formatDateDDMMYYYY, formatDateTimeSlashed, toPhleboGender } from "../HVTCommonHelpers";
import { DEFAULT_VALUES, STEPS, getStepFields } from "./wizardConfig";
import StepIndicator from "./StepIndicator";
import StepFooterNav from "./StepFooterNav";
import PincodeGateSection from "./PincodeGateSection";
import PatientDetailsSection from "./PatientDetailsSection";
import LocationSection from "./LocationSection";
import MyClinicAddressSection from "./MyClinicAddressSection";
import TestsSection from "./TestsSection";
import SlotSection from "./SlotSection";
import BookingReviewStep from "./BookingReviewStep";

const HVTRegistrationForm = ({ onClose, onSubmittingChange }) => {
    const userInfo = store.getState();
    const accountDetails = userInfo?.Partner_Account_Details;
    const selectedOrgId = userInfo?.selectedOrganization?.MHL_Org_Id__c;
    const isCashClient = accountDetails?.MHL_Payment_Type__c === "Cash";

    // MAKE IT FALSE FOR PROD
    const BYPASS_TILE_VALIDATIONS_FOR_TESTING = false;
    
    const canUseCustomerLocation = BYPASS_TILE_VALIDATIONS_FOR_TESTING || accountDetails?.MHL_Payment_Type__c === "Credit";
    const clinicPincode = userInfo?.userData?.Pincode;

    const { watch, setValue, trigger, reset } = useFormContext();

    const [mode, setMode] = useState(null);
    const [step, setStep] = useState("collect");
    const stepBodyRef = useRef(null);

    const [salutationOptions, setSalutationOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [isResolvingLocation, setIsResolvingLocation] = useState(false);

    const [serviceablePincodeOrgs, setServiceablePincodeOrgs] = useState([]);
    const [pincodeFetchState, setPincodeFetchState] = useState("loading");

    const ownOrgPincodeSet = useMemo(() => {
        const own = serviceablePincodeOrgs.find((o) => String(o.orgId) === String(selectedOrgId));
        return new Set((own?.pincodes || []).map(String));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceablePincodeOrgs]);

    const allPincodeSet = useMemo(() => {
        return new Set(serviceablePincodeOrgs.flatMap((o) => (o.pincodes || []).map(String)));
    }, [serviceablePincodeOrgs]);

    const canUseMyClinic = BYPASS_TILE_VALIDATIONS_FOR_TESTING || (pincodeFetchState === "ready" && !!clinicPincode && ownOrgPincodeSet.has(String(clinicPincode)));

    const [slots, setSlots] = useState([]);
    const [slotsMessage, setSlotsMessage] = useState("");
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [isBlockingSlot, setIsBlockingSlot] = useState(false);
    const [blockedPatientId, setBlockedPatientId] = useState(null);
    const [blockSecondsLeft, setBlockSecondsLeft] = useState(0);
    const [blockedSlotKey, setBlockedSlotKey] = useState("");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingBalance, setIsCheckingBalance] = useState(false);

    useEffect(() => {
        onSubmittingChange?.(isSubmitting);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting]);

    const gender = watch("gender");
    const salutation = watch("salutation");
    const firstName = watch("firstName");
    const lastName = watch("lastName");
    const mobile = watch("mobile");
    const email = watch("email");
    const dob = watch("dob");
    const visitDate = watch("visitDate");
    const pincode = watch("pincode");
    const locationId = watch("locationId");
    const locationName = watch("locationName");
    const orgId = watch("orgId");
    const addressLine = watch("addressLine") || "";
    const tests = watch("tests") || [];
    const selectedSlot = watch("selectedSlot");

    const gross = tests.reduce((s, t) => s + (parseFloat(t.TEST_AMOUNT) || 0), 0);
    // const discount = tests.reduce((s, t) => s + (parseFloat(t.TEST_DISCOUNT) || 0), 0);
    const discount = 0;
    const net = gross - discount;
    const hasTests = tests.length > 0;

    useEffect(() => {
        if (mode === "clinic") {
            setValue("orgId", selectedOrgId || "");
        } else if (mode === "customer" && pincode) {
            const match = serviceablePincodeOrgs.find((o) => (o.pincodes || []).map(String).includes(String(pincode)));
            setValue("orgId", match?.orgId || "");
        } else {
            setValue("orgId", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, pincode, serviceablePincodeOrgs]);

    useEffect(() => {
        const firstField = stepBodyRef.current?.querySelector(
            "input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled)"
        );
        firstField?.focus();
    }, [step]);

    useEffect(() => {
        HVTRegistrationService.GetSalutationList()
            .then(({ data }) => {
                setSalutationOptions(data?.IsSuccess && data?.Data?.length ? data.Data : []);
            })
            .catch(() => setSalutationOptions([]));
    }, []);

    const fetchServiceablePincodes = () => {
        setPincodeFetchState("loading");
        HVTRegistrationService.GetServiceablePincode()
            .then(({ data }) => {
                const orgs = data?.IsSuccess ? data?.Data?.serviceablePincodes : null;
                if (!Array.isArray(orgs)) {
                    setServiceablePincodeOrgs([]);
                    setPincodeFetchState("error");
                    return;
                }
                setServiceablePincodeOrgs(orgs);
                setPincodeFetchState("ready");
            })
            .catch(() => {
                setServiceablePincodeOrgs([]);
                setPincodeFetchState("error");
            });
    };

    useEffect(() => {
        fetchServiceablePincodes();
    }, []);

    const resolvePincode = (value) => {
        if (!value || String(value).length !== 6) return;
        setIsResolvingLocation(true);
        HVTRegistrationService.GetCircleLocationUsingPincode(value)
            .then(({ data }) => {
                setLocationOptions(data?.IsSuccess && data?.Data?.GetCircleLocation?.length ? data.Data.GetCircleLocation : []);
            })
            .catch(() => setLocationOptions([]))
            .finally(() => setIsResolvingLocation(false));
    };

    useEffect(() => {
        if (mode !== "clinic") return;
        if (!clinicPincode) return;
        setValue("pincode", clinicPincode, { shouldValidate: true });
        resolvePincode(clinicPincode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const fetchSlots = useCallback(() => {
        setIsLoadingSlots(true);
        setSlots([]);
        setSlotsMessage("");
        setValue("selectedSlot", null);
        setBlockedPatientId(null);
        setBlockedSlotKey("");
        setBlockSecondsLeft(0);

        const payload = {
            Pick_Up_Date: formatDateOnly(visitDate),
            Location: locationName,
            Pincode: pincode,
            LocationID: locationId,
            Gender: toPhleboGender(gender),
        };

        HVTRegistrationService.GetPhleboAvailableLocationDecrypt(payload)
            .then(({ data }) => {
                if (data?.IsSuccess && data?.Data?.GetCircleLocation?.length) {
                    setSlots(data.Data.GetCircleLocation);
                } else {
                    setSlots([]);
                    setSlotsMessage("No slots available for the selected date and location." || data?.ResponseMessage);
                }
            })
            .catch(() => {
                setSlots([]);
                setSlotsMessage("Something went wrong while loading slots. Please try again.");
            })
            .finally(() => setIsLoadingSlots(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitDate, locationName, pincode, locationId, gender]);

    useEffect(() => {
        if (locationId && pincode && visitDate && hasTests) {
            fetchSlots();
        } else {
            setSlots([]);
            setSlotsMessage("");
            setValue("selectedSlot", null);
            setBlockedPatientId(null);
            setBlockedSlotKey("");
            setBlockSecondsLeft(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, pincode, visitDate, hasTests, gender]);

    useEffect(() => {
        if (blockSecondsLeft <= 0) return;
        const timer = setInterval(() => {
            setBlockSecondsLeft((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [blockSecondsLeft > 0]);

    useEffect(() => {
        if (blockSecondsLeft === 0 && blockedPatientId) {
            setValue("selectedSlot", null, { shouldValidate: true });
            setBlockedPatientId(null);
            setBlockedSlotKey("");
            toast.error("Your slot hold expired. Please select a slot again.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockSecondsLeft]);

    const handleSelectSlot = (slot) => {
        setValue("selectedSlot", slot, { shouldValidate: true });
    };

    const handleSelectMode = (target) => {
        if (!BYPASS_TILE_VALIDATIONS_FOR_TESTING) {
            if (pincodeFetchState !== "ready") return;
            if (target === "customer" && !canUseCustomerLocation) return;
            if (target === "clinic" && !canUseMyClinic) return;
        }
        if (mode !== null && mode !== target) {
            reset({ ...DEFAULT_VALUES, visitDate: new Date() });
            setLocationOptions([]);
        }
        setMode(target);
    };

    const handleCollectNext = () => {
        if (!mode) return;
        setStep(mode === "customer" ? "pincodeGate" : "patient");
    };

    const handlePincodeGateNext = async () => {
        const valid = await trigger(getStepFields("pincodeGate", mode));
        if (!valid) return;
        resolvePincode(pincode);
        setStep("patient");
    };

    const handlePatientNext = async () => {
        const valid = await trigger(getStepFields("patient", mode));
        if (!valid) return;
        setStep("location");
    };

    const handleLocationNext = async () => {
        const valid = await trigger(getStepFields("location", mode));
        if (!valid) return;
        setStep("tests");
    };

    const handleTestsNext = async () => {
        const valid = await trigger(getStepFields("tests", mode));
        if (!valid) return;

        if (accountDetails?.MHL_Payment_Type__c === "Rolling Advance") {
            setIsCheckingBalance(true);
            try {
                const { data } = await B2BRegistrationService.GetAccountBalanceDetails(accountDetails.MHL_Client_Code__c);

                if (data?.IsSuccess) {
                    const balance = Number(data.Data.BalanceRollingAdvanceAmount || 0);
                    if (balance < net) {
                        toast.error("Insufficient Balance.");
                        return;
                    }
                } else {
                    toast.error("Unable to fetch account balance.");
                    return;
                }
            } catch (error) {
                toast.error("Something went wrong while checking balance.");
                return;
            } finally {
                setIsCheckingBalance(false);
            }
        }

        setStep("slot");
    };

    const handleSlotNext = async () => {
        const valid = await trigger(getStepFields("slot", mode));
        if (!valid) return;

        const currentSlotKey = `${formatDateOnly(visitDate)} ${selectedSlot?.Slot_TIME || ""}`;
        if (blockedPatientId && blockSecondsLeft > 0 && blockedSlotKey === currentSlotKey) {
            setStep("summary");
            return;
        }

        setIsBlockingSlot(true);

        HVTRegistrationService.GenerateUniquePatientId()
            .then(({ data: patientIdResp }) => {
                if (!patientIdResp?.IsSuccess || !patientIdResp?.Data) {
                    toast.error(patientIdResp?.ResponseMessage || "Unable to generate a patient ID. Please try again.");
                    return null;
                }
                const patientId = patientIdResp.Data;

                const payload = {
                    PatientCount: "1",
                    Pincode: pincode,
                    Bookdatetimeslot: `${formatDateOnly(visitDate)} ${selectedSlot.Slot_TIME || ""}.000`,
                    Location: locationName || "",
                    PatientID: patientId,
                    LocationID: locationId || "",
                    Gender: toPhleboGender(gender),
                    SlotDuration: selectedSlot?.SlotDuration || "",
                };

                return HVTRegistrationService.GetAvailabilityPhlebo(payload).then((blockResp) => {
                    if (!blockResp?.data?.IsSuccess) {
                        toast.error(blockResp?.data?.Message || "Unable to block this slot. Please try another.");
                        return;
                    }
                    setBlockedPatientId(patientId);
                    setBlockedSlotKey(`${formatDateOnly(visitDate)} ${selectedSlot?.Slot_TIME || ""}`);
                    setBlockSecondsLeft(5 * 60);
                    setStep("summary");
                });
            })
            .catch(() => toast.error("Something went wrong while blocking the slot."))
            .finally(() => setIsBlockingSlot(false));
    };

    const handleConfirmBooking = () => {
        if (!selectedSlot) {
            toast.error("Missing visit slot details. Please start a new booking.");
            setStep("slot");
            return;
        }
        if (!blockedPatientId) {
            toast.error("Your slot hold has expired. Please go back and select a slot again.");
            setStep("slot");
            return;
        }

        setIsSubmitting(true);

        const patientId = blockedPatientId;

        const payload = {
            Patientid: patientId,
            CallSource: "Partner Portal",
            IsMyClinic: mode === "clinic",
            Scheduledate: selectedSlot.Slot_TIME || "",
            Bookingdate: `${formatDateDDMMYYYY(visitDate)} ${selectedSlot.Slot_TIME || ""}`,
            Mobile: mobile,
            Clientid: accountDetails?.MHL_Client_Code__c || "",
            //  Clientid: isCashClient ?
            //      "COC020523"
            //     : "COC020524",
            Organizationid: orgId,
            Titleid: salutation,
            FirstName: firstName.trim(),
            LastName: lastName.trim(),
            DOB: formatDateDDMMYYYY(dob),
            Genderid: gender,
            Emailid: email ? email.trim() : "",
            Height: "",
            Weight: "",
            AddressLine: (addressLine ? `${addressLine}, ${pincode}` : pincode).trim(),
            Pincodeid: pincode,
            LocationName: locationName || "",
            Circleid: "",
            Phleboid: "",
            orgid: orgId,
            LocationID: locationId || "",
            Notification: "SMS,Whatsapp",
            DiscountType: "No Discount",
            PromoCode: "",
            DocumentType: "",
            DocumentNumber: "",
            Loyalty: "0",
            Wallet: "0",
            HomeCollectionModel: tests,
            TotalReceivables: [
                {
                    GrossAmount: gross,
                    TotalDiscountAmount: discount,
                    NetAmount: net,
                    AmountReceived: isCashClient ? 0 : net,
                    BalanceReceivable: isCashClient ? net : 0,
                },
            ],
            PaymentDetails: [
                {
                    order_id: "",
                    tracking_id: "",
                    bank_ref_no: "",
                    order_status: "Success",
                    failure_message: "",
                    payment_mode: isCashClient ? "pay_at_pickup" : "UPI",
                    card_name: "",
                    status_code: "",
                    status_message: "",
                    currency: "INR",
                    amount: (isCashClient ? net : 0).toFixed(2),
                    billing_name: "",
                    billing_address: "",
                    billing_city: "",
                    billing_state: "",
                    billing_zip: "",
                    billing_country: "",
                    billing_tel: "",
                    billing_email: "",
                    delivery_name: "",
                    delivery_address: "",
                    delivery_city: "",
                    delivery_state: "",
                    delivery_zip: "",
                    delivery_country: "",
                    delivery_tel: "",
                    merchant_param1: "",
                    merchant_param2: "",
                    merchant_param3: "",
                    merchant_param4: "",
                    merchant_param5: "",
                    vault: "",
                    offer_type: "",
                    offer_code: "",
                    discount_value: 0,
                    mer_amount: (isCashClient ? 0 : net).toFixed(2),
                    eci_value: "",
                    retry: "",
                    response_code: "",
                    billing_notes: "",
                    trans_date: formatDateTimeSlashed(new Date()),
                    bin_country: "",
                },
            ],
        };

        HVTRegistrationService.NewRegistration({ data: [payload] })
            .then((regResp) => {
                if (!regResp?.data?.IsSuccess) {
                    toast.error(regResp?.data?.Message || "Registration failed.");
                    return;
                }

                const bookingId = regResp?.data?.Data?.[0]?.Home_Status || null;
                setBlockSecondsLeft(0);
                setBlockedPatientId(null);

                toast.success(`Booking confirmed successfully. Booking ID: ${bookingId}`);
                onClose();
            })
            .catch(() => toast.error("Something went wrong while submitting the registration."))
            .finally(() => setIsSubmitting(false));
    };

    const handleConfirmClick = async () => {
        const valid = await trigger();
        if (!valid) {
            toast.error("Please review the form — some details are missing or invalid.");
            return;
        }
        handleConfirmBooking();
    };

    const footerConfig = {
        collect: { showBack: false, onNext: handleCollectNext, disabled: !mode },
        pincodeGate: { onBack: () => setStep("collect"), onNext: handlePincodeGateNext },
        patient: {
            onBack: () => setStep(mode === "customer" ? "pincodeGate" : "collect"),
            onNext: handlePatientNext,
        },
        location: { onBack: () => setStep("patient"), onNext: handleLocationNext },
        tests: { onBack: () => setStep("location"), onNext: handleTestsNext, isNextLoading: isCheckingBalance },
        slot: { onBack: () => setStep("tests"), onNext: handleSlotNext, nextLabel: "Review booking", isNextLoading: isBlockingSlot },
        summary: {
            onBack: () => setStep("slot"),
            onNext: handleConfirmClick,
            nextLabel: "Confirm booking",
            isNextLoading: isSubmitting,
            disabled: !selectedSlot,
            backDisabled: isSubmitting,
        },
    }[step];

    return (
        <>
        <div className="hvt-wizard-scroll">
            {pincodeFetchState === "loading" && (
                <div className="hvt-wizard-loading-overlay">
                    <FaSpinner className="fa-spin" />
                    Checking service availability...
                </div>
            )}
            <StepIndicator
                steps={STEPS.filter((s) => s.id !== "pincodeGate" || mode === "customer")}
                currentStepId={step}
            />

            {blockSecondsLeft > 0 && (
                <div className={`hvt-slot-hold-strip ${blockSecondsLeft <= 60 ? "is-urgent" : ""}`}>
                    <Clock size={14} />
                    <span>Slot held</span>
                    <span className="hvt-slot-hold-time">
                        {Math.floor(blockSecondsLeft / 60)}:{String(blockSecondsLeft % 60).padStart(2, "0")}
                    </span>
                </div>
            )}

            {step === "collect" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <div className="hvt-collect-wrap">
                        {pincodeFetchState === "error" && (
                            <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 py-2 px-3 mb-3" style={{ fontSize: 13 }}>
                                <span>Unable to verify home-visit service availability.</span>
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchServiceablePincodes}>Retry</button>
                            </div>
                        )}
                        <div className="hvt-collect-row d-flex gap-2">
                            <div
                                className={`hvt-collect-tile ${mode === "clinic" ? "active" : ""} ${!BYPASS_TILE_VALIDATIONS_FOR_TESTING && (pincodeFetchState !== "ready" || !canUseMyClinic) ? "disabled" : ""}`}
                                onClick={() => handleSelectMode("clinic")}
                                title={pincodeFetchState === "ready" && !canUseMyClinic ? "Home-visit service isn't available for your clinic's pincode" : undefined}
                            >
                                <div className="hvt-collect-tile-icon"><FaBuilding /></div>
                                <div className="title">My clinic</div>
                                <div className="subtitle">Walk-in registration</div>
                                <div className="description">In-clinic visit</div>
                            </div>
                            <div
                                className={`hvt-collect-tile ${mode === "customer" ? "active" : ""} ${!BYPASS_TILE_VALIDATIONS_FOR_TESTING && (pincodeFetchState !== "ready" || !canUseCustomerLocation) ? "disabled" : ""}`}
                                onClick={() => handleSelectMode("customer")}
                                title={pincodeFetchState === "ready" && !canUseCustomerLocation ? "Available only for credit-billed accounts" : undefined}
                            >
                                <div className="hvt-collect-tile-icon"><FaMapMarkerAlt /></div>
                                <div className="title">Customer location</div>
                                <div className="subtitle">Enter address</div>
                                <div className="description">At-home visit</div>
                            </div>
                        </div>
                        <div className="hvt-step-note">
                            <FaInfoCircle size={13} />
                            Choose My Clinic for a walk-in registration, or Customer Location to visit the patient at their address.
                        </div>
                    </div>
                </div>
            )}

            {step === "pincodeGate" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <PincodeGateSection serviceablePincodeSet={allPincodeSet} />
                    <div className="hvt-step-note">
                        <FaInfoCircle size={13} />
                        Home-visit service is only available on  select pincodes — enter one to check availability.
                    </div>
                </div>
            )}

            {step === "patient" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <PatientDetailsSection salutationOptions={salutationOptions} />
                </div>
            )}

            {step === "location" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    {mode === "clinic" ? (
                        <MyClinicAddressSection
                            locationOptions={locationOptions}
                            isResolvingLocation={isResolvingLocation}
                        />
                    ) : (
                        <LocationSection
                            locationOptions={locationOptions}
                            isResolvingLocation={isResolvingLocation}
                        />
                    )}
                    <div className="hvt-step-note">
                        <FaInfoCircle size={13} />
                        Make sure the address is accurate — this is where the phlebotomist will visit.
                    </div>
                </div>
            )}

            {step === "tests" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <TestsSection />
                    <div className="hvt-step-note">
                        <FaInfoCircle size={13} />
                        Search by test name or code, or pick from the frequently booked tests above.
                    </div>
                </div>
            )}

            {step === "slot" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <SlotSection
                        slots={slots}
                        slotsMessage={slotsMessage}
                        isLoadingSlots={isLoadingSlots}
                        onSelectSlot={handleSelectSlot}
                    />
                    <div className="hvt-step-note">
                        <FaInfoCircle size={13} />
                        Once you click Review booking, the slot is held for 5 minutes — confirm the booking before the hold expires.
                    </div>
                </div>
            )}

            {step === "summary" && (
                <div className="hvt-step-body" ref={stepBodyRef}>
                    <BookingReviewStep />
                </div>
            )}

        </div>

        {footerConfig && (
            <div className="hvt-wizard-footer">
                <StepFooterNav {...footerConfig} />
            </div>
        )}
        </>
    );
};

export default HVTRegistrationForm;
